import {
  ACTIVE_IMAGE_MODERATION_CHAIN,
  ACTIVE_TEXT_MODERATION_CHAIN,
  CHAIN_POSITIONS,
  MODERATION_PROVIDER_PRICING,
  type ModerationProviderId,
} from "./ModerationProviderPricing";
import pricingSettingsJson from "./PricingSettings.json";
import type { PricingSettings } from "./PricingSettingsSchema";

/**
 * Planning decisions used by the development-only back office Pricing tab. Stripe prices are read
 * from Stripe, provider list prices live in ProviderPricing.ts, physical constants and Stripe's fee
 * rates are code constants and traffic-dependent values are measured by the admin pricing endpoint
 * (PricingAssumptions.ts). Only what cannot be measured or derived is a setting here.
 */

export type { PricingSettings } from "./PricingSettingsSchema";

/** Chain-share settings, named after the provider at each position of the active chain. */
const chainShareDescriptions = (type: "image" | "text", chain: readonly ModerationProviderId[]) =>
  Object.fromEntries(
    CHAIN_POSITIONS.map((position, index) => {
      const provider = chain[index];
      const name = provider ? MODERATION_PROVIDER_PRICING[provider].name : "unused";
      const role = index === 0 ? "primary" : index === 1 ? "first fallback" : "second fallback";
      return [
        `chainShares.${type}.${position}`,
        {
          label: `${type === "image" ? "Image" : "Text"} share: ${role} (${name})`,
          description: `Share of ${type} moderation requests served by the ${role}, currently ${name}.`,
        },
      ];
    }),
  );

export const PRICING_SETTINGS_DESCRIPTIONS: Record<string, { label: string; description: string }> =
  {
    searchIndexingRepairMultiplier: {
      label: "Search repair reserve",
      description: "Multiplier covering expected automatic repair work; 1.25 reserves 25%.",
    },
    searchVectorRetentionMonths: {
      label: "Search vector retention allocation",
      description:
        "Months of vector storage allocated to each indexing unit; review against actual retention.",
    },
    searchDecoderSecondsPerMediaSecond: {
      label: "Search decoder runtime per media second",
      description: "Allocated container runtime per indexed media second.",
    },
    ...chainShareDescriptions("image", ACTIVE_IMAGE_MODERATION_CHAIN),
    ...chainShareDescriptions("text", ACTIVE_TEXT_MODERATION_CHAIN),
  };

// Not parsed here: the landing page's pricing calculator reaches this module, and parsing would
// load zod in every visitor's browser. PricingEconomics.test.ts checks the JSON against the schema.
export const DEFAULT_PRICING_SETTINGS = pricingSettingsJson as PricingSettings;

/** Lists every numeric setting as a dotted path, in file order. */
export const getPricingSettingPaths = (settings: PricingSettings): string[] => {
  const paths: string[] = [];
  const visit = (value: unknown, prefix: string) => {
    if (typeof value === "number") {
      paths.push(prefix);
      return;
    }
    if (value && typeof value === "object") {
      for (const [key, child] of Object.entries(value)) {
        visit(child, prefix ? `${prefix}.${key}` : key);
      }
    }
  };
  visit(settings, "");
  return paths;
};

export const getPricingSettingValue = (settings: PricingSettings, path: string): number => {
  let current: unknown = settings;
  for (const key of path.split(".")) {
    if (!current || typeof current !== "object" || !(key in current)) {
      throw new Error(`Unknown pricing setting: ${path}`);
    }
    current = (current as Record<string, unknown>)[key];
  }
  if (typeof current !== "number") {
    throw new Error(`Pricing setting is not a number: ${path}`);
  }
  return current;
};

/** Returns a copy of the settings with one numeric setting replaced. Does not validate. */
export const setPricingSettingValue = (
  settings: PricingSettings,
  path: string,
  value: number,
): PricingSettings => {
  getPricingSettingValue(settings, path);
  const copy = structuredClone(settings);
  const keys = path.split(".");
  const lastKey = keys.pop() as string;
  let current = copy as unknown as Record<string, unknown>;
  for (const key of keys) {
    current = current[key] as Record<string, unknown>;
  }
  current[lastKey] = value;
  return copy;
};
